import { useCallback, useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setSelectedCatalogue } from '../../../redux/jobs/action'
import debounce from '../../../utils/debounce'
import {
  include,
  isEqual,
  notEqual,
  ternary,
  values,
} from '../../../utils/javascript'
import { hasOnlySpace } from '../../../utils/validation'
import {
  addBrandApi,
  getBrandListApi,
  searchBrandApi,
} from '../../catalogueManagement/catalogue.api'
import {
  payloadType,
  tabKeys,
} from '../../catalogueManagement/catalogue.description'

const catalogueSelector = ({ index }) => {
  const { t } = useTranslations()
  const { dispatch, selector } = useRedux()
  const selectedState = selector(state => state?.jobs?.catalogueSelected)
  const selected = selectedState?.[index]
  const [selectState, setSelectState] = useState({})
  const {
    [tabKeys.brand]: brand,
    [tabKeys.productSeries]: productSeries,
    [tabKeys.model]: model,
  } = {
    ...selectState,
  }
  const brandId = selected?.brand?.value
  const productSeriesId = selected?.productSeries?.value
  const modelId = selected?.model?.value
  const [tempSelected, setTempSelected] = useState({})
  const [popup, setPopup] = useState({ open: false, message: '' })
  const [isDisable, setIsDisable] = useState(false)
  const [isOpen, setIsOpen] = useState({})
  const [searchValues, setSearchValues] = useState({})
  const [addLoader, setAddLoader] = useState(false)
  const pageSize = 10

  useEffect(() => {
    getSelectOptions({ type: tabKeys.brand })

    if (brandId) {
      getSelectOptions({
        type: tabKeys.productSeries,
        parentId: brandId,
      })
    }
    if (productSeriesId) {
      getSelectOptions({
        type: tabKeys.model,
        parentId: productSeriesId,
      })
    }
  }, [])

  useEffect(() => {
    if (brandId && !productSeriesId) {
      getSelectOptions({
        type: tabKeys.productSeries,
        parentId: brandId,
      })
    }
    if (productSeriesId && !modelId) {
      getSelectOptions({
        type: tabKeys.model,
        parentId: productSeriesId,
      })
    }
  }, [brandId, productSeriesId])

  const getParams = ({ pageNo = 1, type, parentId }) => {
    let params = `${pageNo}?pageSize=${pageSize}&type=${payloadType[type]}`
    if (include([tabKeys.productSeries, tabKeys.model], type))
      params += `&parentId=${parentId}`
    return params
  }

  const getSelectOptions = async ({ pageNo = 1, type, parentId, search }) => {
    setSelectState(prev => ({
      ...prev,
      [type]: {
        ...prev?.[type],
        loader: true,
      },
    }))

    const params = getParams({ type, parentId, pageNo })
    let resp
    if (search?.trim()) {
      resp = await searchBrandApi({ params: `${params}&name=${search}` })
    } else {
      resp = await getBrandListApi({ params })
    }
    setSelectState(prev => ({
      ...prev,
      [type]: {
        ...resp?.data,
        list: [
          ...((notEqual(pageNo, 1) && prev?.[type]?.list) || []),
          ...(resp?.data?.list || []),
        ],
        loader: false,
      },
    }))
    setIsDisable(false)
  }

  const debounceGetOptions = useCallback(debounce(getSelectOptions, 500), [])

  const onScroll = debounce((e, type) => {
    if (isDisable) return
    const { target } = e
    const { pageNo, hasMore } = { ...selectState?.[type] }
    if (
      hasMore &&
      isEqual(target.scrollHeight - target.scrollTop, target.clientHeight)
    ) {
      let parentId
      if (isEqual(tabKeys.productSeries, type)) parentId = brandId
      if (isEqual(tabKeys.model, type)) parentId = productSeriesId
      getSelectOptions({
        pageNo: pageNo + 1,
        type,
        parentId,
        search: searchValues?.[type],
      })
    }
  }, 300)

  const handleSelectChange = ({ type, option, showConfirmation }) => {
    if (showConfirmation) {
      setPopup({
        open: true,
        message: t('msg_AreYouSureWantToChangeSelection'),
      })
      return setTempSelected({
        type,
        option,
      })
    }

    let newSelected = {}
    if (isEqual(type, tabKeys.brand)) {
      newSelected = {
        brand: option,
        productSeries: null,
        model: null,
      }
    } else if (isEqual(type, tabKeys.productSeries)) {
      newSelected = {
        productSeries: option,
        model: null,
      }
    } else if (isEqual(type, tabKeys.model)) {
      newSelected = {
        model: option,
      }
    }
    const updatedSelection = [...(selectedState || [])]
    updatedSelection[index] = { ...updatedSelection[index], ...newSelected }
    dispatch(setSelectedCatalogue(updatedSelection))
  }

  const handleClosePopup = () => setPopup({ open: false, message: '' })

  const onInputSearch = async (search, type) => {
    setIsDisable(true)
    setSearchValues({ ...searchValues, [type]: search })
    let parentId
    if (isEqual(tabKeys.productSeries, type)) parentId = brandId
    if (isEqual(tabKeys.model, type)) parentId = productSeriesId
    if (notEqual(tabKeys.brand, type) && !parentId) return setIsDisable(false)
    !hasOnlySpace(search) && debounceGetOptions({ parentId, search, type })
  }

  const onDropdownVisible = visible => {
    setIsOpen({ ...isOpen, ...visible })
  }

  const handleAddNewItem = async ({ tabKey, showConfirmation }) => {
    const search = searchValues?.[tabKey]?.trim()
    if (!search) return
    setAddLoader(true)
    let params = `?source=JOB&shortDescription= &longDescription= &name=${search}`
    if (include([tabKeys.productSeries, tabKeys.model], tabKey))
      params += `&brandId=${brandId}`
    if (isEqual(tabKeys.model, tabKey))
      params += `&productSeriesId=${productSeriesId}`
    const resp = await addBrandApi({ params })
    setAddLoader(false)
    const value = resp?.data?.id
    if (value) {
      handleSelectChange({
        type: tabKey,
        option: { label: search, value },
        showConfirmation,
      })
    }
    getSelectOptions({
      type: tabKey,
      parentId: ternary(
        isEqual(tabKey, tabKeys.productSeries),
        brandId,
        ternary(isEqual(tabKey, tabKeys.model), productSeriesId, null),
      ),
    })
    setIsOpen({ ...isOpen, [tabKey]: false })
    setSearchValues({ ...searchValues, [tabKey]: '' })
  }

  const modifyForOptions = (selectedOption, type) => {
    const list = selectState?.[type]?.list
    const newList = []
    const isSearchList = searchValues?.[type]?.trim()
    list?.forEach(({ name, id }) => {
      if (isEqual(selectedOption?.value, id) && !isSearchList) return
      newList.push({
        label: name,
        value: id,
      })
    })
    return ternary(
      selectedOption?.value && !isSearchList,
      [selectedOption, ...newList],
      newList,
    )
  }

  const brandOptions = modifyForOptions(selected?.brand, tabKeys.brand)
  const productOptions = modifyForOptions(
    selected?.productSeries,
    tabKeys.productSeries,
  )
  const modelOptions = modifyForOptions(selected?.model, tabKeys.model)

  const selectAttributes = [
    {
      className: 'brand-select',
      label: t('txt_Brand'),
      placeholder: t('ctlg_SelectBrand'),
      searchPlaceholder: t('ctlg_SearchBrand'),
      tabKey: tabKeys.brand,
      value: brandId,
      options: brandOptions,
      loading: brand?.loader,
    },
    {
      className: 'product-select',
      label: t('txt_ProductSeries'),
      placeholder: t('ctlg_SelectProductSeries'),
      searchPlaceholder: t('ctlg_SearchProductSeries'),
      tabKey: tabKeys.productSeries,
      value: productSeriesId,
      options: productOptions,
      loading: productSeries?.loader,
      disableInput: !brandId,
    },
    {
      className: 'product-select',
      label: t('txt_Model'),
      placeholder: t('job_SelectModel'),
      searchPlaceholder: t('ctlg_SearchModel'),
      tabKey: tabKeys.model,
      value: modelId,
      options: modelOptions,
      loading: model?.loader,
      disableInput: !productSeriesId,
    },
  ]

  const loader = values(selectState)?.some(value => value?.loader)

  return {
    t,
    popup,
    loader,
    isOpen,
    addLoader,
    tempSelected,
    searchValues,
    selectAttributes,
    onScroll,
    onInputSearch,
    handleClosePopup,
    handleAddNewItem,
    onDropdownVisible,
    handleSelectChange,
  }
}

export default catalogueSelector
